/**
 * Nested ELK layout for Compass infra views.
 *
 * Strategy: the existing `InfraLayoutEngine` classifies resources
 * (tiers, subnets, VPCs, stacks, shared lane) and emits a
 * classification + an initial grid placement. We take the
 * CLASSIFICATION (frames, frameShapes, clusters, shared refs) and ask
 * ELK for the GEOMETRY. ELK gets a full nested graph:
 *
 *     root
 *     ├── vpc-a  (compound)
 *     │   ├── ingress leaves (ALB, IGW, …)
 *     │   ├── tier-group: public  (compound, optional)
 *     │   │   ├── subnet public-a  (compound)
 *     │   │   │   └── leaves / cluster reps
 *     │   │   └── subnet public-b  (compound)
 *     │   ├── tier-group: private (compound, optional)
 *     │   └── floor leaves (SGs attached to the VPC, …)
 *     ├── vpc-b  (compound)
 *     └── shared lane leaves (IAM, S3, KMS, …)
 *
 * All flow edges live on the root graph. `hierarchyHandling:
 * INCLUDE_CHILDREN` lets ELK route edges across container boundaries
 * cleanly — this is the thing a hand-rolled router can't do.
 *
 * ELK returns: absolute positions for every leaf, bounds for every
 * compound node (→ our frames), and orthogonal polylines for every
 * edge (→ `layoutPoints` on scene edges).
 *
 * Server-safe note: this file imports elkjs and therefore ONLY runs
 * in the browser. The server path (CompassViewMaterializer) still
 * uses `InfraLayoutEngine.layout()` directly without this enhancer.
 */

import ELK from 'elkjs/lib/elk.bundled.js';
import type {
  InfraLayoutOutput,
  InfraLayoutEdge,
  InfraFrame,
} from '../InfraLayoutEngine';

const elk = new ELK();

export interface ElkInfraLayoutInput {
  engineOutput: InfraLayoutOutput;
  flowEdges: InfraLayoutEdge[];
  /** Shape size lookup. Clusters in `engineOutput.clusters` carry
   *  their own width/height; everything else falls through here. */
  shapeSize: (id: string) => { width: number; height: number } | undefined;
}

export interface ElkInfraLayoutResult {
  /** Leaf / cluster-rep positions, absolute (ELK origin = 0,0). */
  positions: Map<string, { x: number; y: number }>;
  /** Frames (VPC / tier-group / subnet), with bounds from ELK. */
  frames: Map<string, InfraFrame>;
  /** Edge id → flat `[x,y,x,y,...]` polyline in absolute coords. */
  edgeRoutes: Map<string, number[]>;
}

const ELK_TIMEOUT_MS = 5000;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`ELK timeout ${ms}ms`)), ms);
    p.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

/** Padding applied to each compound node so the child content doesn't
 *  collide with the frame header. Values mirror the engine's visual
 *  padding so the ELK-produced frame bounds land in the same ballpark. */
const FRAME_PADDING: Record<string, string> = {
  vpc: '[top=44,left=24,bottom=24,right=24]',
  'tier-group': '[top=32,left=14,bottom=14,right=14]',
  subnet: '[top=36,left=20,bottom=20,right=20]',
  account: '[top=44,left=24,bottom=24,right=24]',
  region: '[top=44,left=24,bottom=24,right=24]',
};

export async function runElkInfraLayout(
  input: ElkInfraLayoutInput,
): Promise<ElkInfraLayoutResult | null> {
  const { engineOutput, flowEdges, shapeSize } = input;

  // ── Build parent map ────────────────────────────────────────
  // `frameShapes` keyed by container id → list of children (leaves
  // AND nested frames, depending on the container). Invert to get
  // childId → parentFrameId. Roots (no entry) bubble up to 'root'.
  const parentOf = new Map<string, string>();
  for (const [frameId, children] of engineOutput.frameShapes) {
    for (const childId of children) parentOf.set(childId, frameId);
  }
  // `tier-group:<vpcId>:<tier>` frames are not listed inside any
  // VPC's frameShapes (VPC only owns ingress/floor leaves) — infer
  // the parent VPC from the synthetic id.
  for (const [frameId, frame] of engineOutput.frames) {
    if (frame.resourceType !== 'tier-group') continue;
    if (parentOf.has(frameId)) continue;
    const parts = frameId.split(':');
    if (parts.length >= 3) {
      const vpcId = parts[1];
      if (engineOutput.frames.has(vpcId)) parentOf.set(frameId, vpcId);
    }
  }
  // Subnets w/o a tier-group parent still belong to some VPC. Use
  // spatial containment on the engine's initial placement to find it.
  for (const [frameId, frame] of engineOutput.frames) {
    if (frame.resourceType !== 'subnet') continue;
    if (parentOf.has(frameId)) continue;
    const cx = frame.x + frame.width / 2;
    const cy = frame.y + frame.height / 2;
    for (const [otherId, other] of engineOutput.frames) {
      if (other.resourceType !== 'vpc') continue;
      if (
        cx >= other.x && cx <= other.x + other.width &&
        cy >= other.y && cy <= other.y + other.height
      ) {
        parentOf.set(frameId, otherId);
        break;
      }
    }
  }

  // ── Aggregate nodes and group by parent ─────────────────────
  const allNodes = new Set<string>();
  for (const fid of engineOutput.frames.keys()) allNodes.add(fid);
  for (const pid of engineOutput.positions.keys()) allNodes.add(pid);

  const childrenByParent = new Map<string, string[]>();
  const ROOT = '__root__';
  for (const id of allNodes) {
    const p = parentOf.get(id) ?? ROOT;
    const arr = childrenByParent.get(p) ?? [];
    arr.push(id);
    childrenByParent.set(p, arr);
  }

  // ── Build ELK graph recursively ─────────────────────────────
  type ElkChild = {
    id: string;
    width?: number;
    height?: number;
    children?: ElkChild[];
    layoutOptions?: Record<string, string>;
  };

  const buildNode = (id: string): ElkChild => {
    const frame = engineOutput.frames.get(id);
    const kids = (childrenByParent.get(id) ?? []).map(buildNode);
    if (frame) {
      const padding = FRAME_PADDING[frame.resourceType] ?? FRAME_PADDING.subnet;
      const nodeOpts: Record<string, string> = {
        'elk.padding': padding,
        // Force per-subtree layered layout so siblings at the same
        // level are laid out together. Without this, ELK can skip
        // layering inside small compounds and leave things stacked.
        'elk.algorithm': 'layered',
        'elk.direction': 'RIGHT',
        'elk.layered.spacing.nodeNodeBetweenLayers': '50',
        'elk.spacing.nodeNode': '30',
      };
      // Subnets under a tier-group stack horizontally as siblings; the
      // tier-group enforces its own direction. VPC-level stacks tiers
      // top→bottom so they read public / private / data.
      if (frame.resourceType === 'vpc') nodeOpts['elk.direction'] = 'DOWN';
      if (frame.resourceType === 'tier-group') nodeOpts['elk.direction'] = 'RIGHT';
      return {
        id,
        children: kids.length > 0 ? kids : undefined,
        layoutOptions: nodeOpts,
      };
    }
    // Leaf: lookup width/height. Cluster reps are also leaves from
    // ELK's perspective; the shapeSize callback handles both.
    const size = shapeSize(id) ?? { width: 220, height: 60 };
    return { id, width: size.width, height: size.height };
  };

  const rootKids = (childrenByParent.get(ROOT) ?? []).map(buildNode);

  const rootEdges = flowEdges
    .filter((e) =>
      allNodes.has(e.source) &&
      allNodes.has(e.target) &&
      e.source !== e.target,
    )
    .map((e) => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    }));

  const graph = {
    id: 'infra-root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'DOWN',
      'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.spacing.nodeNode': '60',
      'elk.layered.spacing.nodeNodeBetweenLayers': '80',
      'elk.spacing.edgeNode': '24',
      'elk.spacing.edgeEdge': '16',
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    },
    children: rootKids,
    edges: rootEdges,
  };

  let result: any;
  try {
    result = await withTimeout(elk.layout(graph as any), ELK_TIMEOUT_MS);
  } catch {
    return null;
  }

  // ── Walk the result tree → absolute positions + frame bounds ──
  const positions = new Map<string, { x: number; y: number }>();
  const frames = new Map<string, InfraFrame>();
  const walk = (node: any, offsetX: number, offsetY: number): void => {
    const absX = (node.x ?? 0) + offsetX;
    const absY = (node.y ?? 0) + offsetY;
    const origFrame = engineOutput.frames.get(node.id);
    if (origFrame) {
      frames.set(node.id, {
        ...origFrame,
        x: absX,
        y: absY,
        width: node.width ?? origFrame.width,
        height: node.height ?? origFrame.height,
      });
    } else {
      positions.set(node.id, { x: absX, y: absY });
    }
    for (const kid of node.children ?? []) walk(kid, absX, absY);
  };
  for (const kid of result.children ?? []) walk(kid, 0, 0);

  // ── Collect edge routes with proper coordinate offsets ───────
  // ELK emits edges on whatever subgraph the layout engine places
  // them. Coordinates are RELATIVE to that subgraph's origin — walk
  // down and accumulate the offset so final points are absolute.
  const edgeRoutes = new Map<string, number[]>();
  const collectEdges = (node: any, offsetX: number, offsetY: number): void => {
    const edges = node.edges as Array<{
      id?: string;
      sections?: Array<{
        startPoint?: { x?: number; y?: number };
        endPoint?: { x?: number; y?: number };
        bendPoints?: Array<{ x?: number; y?: number }>;
      }>;
    }> | undefined;
    if (edges) {
      for (const e of edges) {
        if (!e.id || !e.sections || e.sections.length === 0) continue;
        const pts: number[] = [];
        for (const sec of e.sections) {
          if (sec.startPoint) {
            pts.push(
              (sec.startPoint.x ?? 0) + offsetX,
              (sec.startPoint.y ?? 0) + offsetY,
            );
          }
          for (const bp of sec.bendPoints ?? []) {
            if (bp.x === undefined || bp.y === undefined) continue;
            pts.push(bp.x + offsetX, bp.y + offsetY);
          }
          if (sec.endPoint) {
            pts.push(
              (sec.endPoint.x ?? 0) + offsetX,
              (sec.endPoint.y ?? 0) + offsetY,
            );
          }
        }
        if (pts.length >= 4) edgeRoutes.set(e.id, pts);
      }
    }
    for (const kid of node.children ?? []) {
      collectEdges(kid, offsetX + (kid.x ?? 0), offsetY + (kid.y ?? 0));
    }
  };
  collectEdges(result, 0, 0);

  return { positions, frames, edgeRoutes };
}
